import { TOKEN_ADDRESSES, TOKEN_CONFIG, getGatewayContractAddress, getTokenAddress } from "@/config";
import { PENDLE_MARKETS } from "@/config/pendle.config";
import { PendleService } from "@/services/real/pendle/pendle.service";
import { COMMON_TOKEN_IDS, PENDLE_PT_LOCK_TOKEN_IDS, type TokenId } from "@/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { type Address, type Hash, parseUnits, formatUnits } from "viem";
import { mainnet } from "viem/chains";
import { useWalletClient } from "wagmi";

interface useZapProps {
    sourceTokenId: TokenId;
    amount: number;
    outTokenId?: TokenId;
}

const tokenOut = COMMON_TOKEN_IDS.USDC;

export const useGatewayPendleZap = ({ sourceTokenId, amount: amountIn }: useZapProps) => {
    const isPendlePt = useMemo(() => sourceTokenId in PENDLE_PT_LOCK_TOKEN_IDS, [sourceTokenId]);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { data: walletClient } = useWalletClient();

    const [amountOut, setAmountOut] = useState<number>();

    const getPendleSwap = useCallback(
        async () =>
            await PendleService.swap({
                chainId: mainnet.id,
                receiver: getGatewayContractAddress(mainnet.id),
                enableAggregator: true,
                aggregators: ["kyberswap", "okx", "odos", "paraswap"],
                amountIn: parseUnits(`${amountIn}`, TOKEN_CONFIG[sourceTokenId].decimals),
                tokenIn: getTokenAddress(mainnet.id, sourceTokenId),
                tokenOut: TOKEN_ADDRESSES[mainnet.id].USDC!,
                marketAddress: PENDLE_MARKETS[sourceTokenId as keyof typeof PENDLE_MARKETS],
                slippage: 0.01,
            }).then(({ tx, contractCallParams, contractCallParamsName, data: { amountOut } }) => {
                const outputIndex = contractCallParamsName.findIndex((param) => param === "output");
                const outputData: {
                    swapData: {
                        extRouter: Address;
                        extCalldata: Hash;
                    };
                    amountOut: string;
                } = {
                    ...contractCallParams[outputIndex],
                    amountOut,
                    tx,
                };

                return outputData as {
                    swapData: {
                        extRouter: Address;
                        extCalldata: Hash;
                    };
                    amountOut: string;
                    tx: {
                        to: Address;
                        data: Hash;
                    };
                };
            }),
        [amountIn, sourceTokenId, tokenOut],
    );

    useEffect(() => {
        if (!walletClient) return;
        if (isLoading) return;
        if (amountOut !== undefined) return;
        if (!isPendlePt) return;
        setIsLoading(true);
        getPendleSwap()
            .then(({ amountOut: currentAmountOut }) => {
                const bigCurrentAmountOut = BigInt(currentAmountOut);
                setAmountOut(Number.parseFloat(formatUnits(bigCurrentAmountOut, TOKEN_CONFIG[tokenOut].decimals)));
            })
            .catch((error) => {
                console.error("Failed to fetch Pendle swap:", error);
                // Reset amount out on error
                setAmountOut(undefined);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [getPendleSwap, walletClient, isPendlePt, amountOut, isLoading]);

    return {
        amountOut,
        tokenOut,
        requiresZap: isPendlePt,
        isLoading,
        getPendleSwap,
    };
};
